'use client'

import React from 'react'
import { useTheme } from '../theme/provider'

export interface DarkModeToggleProps {
  /**
   * Custom className for styling
   */
  className?: string
  /**
   * Show label text
   * @default false
   */
  showLabel?: boolean
  /**
   * Custom labels (default: "Light" and "Dark")
   */
  labels?: {
    light: string
    dark: string
  }
}

/**
 * Dark Mode Toggle Component
 * A button to toggle between light and dark themes
 *
 * @example
 * ```tsx
 * import { DarkModeToggle } from '@next-wordpress/ui'
 *
 * function Header() {
 *   return (
 *     <header>
 *       <DarkModeToggle showLabel />
 *     </header>
 *   )
 * }
 * ```
 */
export function DarkModeToggle({
  className = '',
  showLabel = false,
  labels = { light: 'Light', dark: 'Dark' },
}: DarkModeToggleProps) {
  const { darkMode, toggleDarkMode, theme } = useTheme()

  const primaryColor = theme.colors.primary

  return (
    <button
      onClick={toggleDarkMode}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-300 hover:shadow-md ${className}`}
      style={{
        borderColor: darkMode ? theme.colors.border : '#e5e7eb',
        backgroundColor: darkMode ? theme.colors.surface : '#ffffff',
        color: darkMode ? theme.colors.text : '#374151',
      }}
      aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {/* Sun Icon (Light Mode) */}
      <svg
        className={`w-5 h-5 transition-all duration-300 ${
          darkMode ? 'scale-0 opacity-0 absolute' : 'scale-100 opacity-100'
        }`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        style={{ color: primaryColor }}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
        />
      </svg>

      {/* Moon Icon (Dark Mode) */}
      <svg
        className={`w-5 h-5 transition-all duration-300 ${
          darkMode ? 'scale-100 opacity-100' : 'scale-0 opacity-0 absolute'
        }`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        style={{ color: primaryColor }}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
        />
      </svg>

      {showLabel && (
        <span className="text-sm font-medium">
          {darkMode ? labels.dark : labels.light}
        </span>
      )}
    </button>
  )
}
